// web/src/store/marketData/marketDataThunks.ts
import toastMessage from '@/components/Toast';
import {
  CorrelatedMarketDataPayload,
  DiscountFactorsPayload,
  FxAnalysisPayload,
  FxSpotHistoryReportPayload,
  HistoricalIrDifferencialByCurrencyPayload,
} from '@/pages/MarketCurvesCalculation/store/types';
import downloadS3File from '@/utils/downloadS3File';
import apiInstance from '@api/index';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { getThunkError } from '../utility/thunkErrorUtils';

import {
  DownloadCDSCurveReportPayloadTypes,
  DownloadHistoricalForwardCurveReportPayloadTypes,
  DownloadInterestCostCalcReportPayloadTypes,
  DownloadIrAnalysisPayloadTypes,
  DownloadIrDifferencialCurrencyReportPaylaodTypes,
  DownloadIrForwardCurveReportPayloadTypes,
  DownloadSecuritiesDataReportPayloadTypes,
  FileS3ResponseTypes,
  FxOptionVolatilityBackendRequest,
  IrCapletVolatilityBackendRequest,
  IrDiffByCurrencyResponseTypes,
} from './types';

// Define the FilterOption interface if not defined elsewhere
interface FilterOption {
  label: string;
  value: string;
}

// Thunk to fetch IR Index List
export const fetchIrIndexList = createAsyncThunk<
  FilterOption[],
  void,
  { rejectValue: string }
>('marketData/fetchIrIndexList', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.fetchIrIndexList();
    const data: any[] = response.data.data;
    const irIndexList: FilterOption[] = data.map((item) => ({
      label: item.description ?? `Option ${item.index_id}`,
      value: item.index_id ?? '',
    }));
    return irIndexList;
  } catch (error: any) {
    const message = error.message || 'Failed to fetch IR index list';
    toastMessage.error(message);
    return rejectWithValue(message);
  }
});

// Thunk to fetch Closest Trade Valuation Date
export const fetchClosestTradeValuationDate = createAsyncThunk<
  { value_date: string },
  string | undefined,
  { rejectValue: string }
>(
  'marketData/fetchClosestTradeValuationDate',
  async (date, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.irAnalysis.getClosestTradeValuationDateByDate(
          `${date}`,
        );
      const data = response.data;
      return { value_date: data.data };
    } catch (error: any) {
      const message =
        error.message || 'Failed to fetch closest trade valuation date';
      toastMessage.error(message);
      return rejectWithValue(message);
    }
  },
);

// Thunk to validate IR Index
export const validateIrIndex = createAsyncThunk<
  { [key: string]: boolean },
  { date: string; index_id_list: string[] },
  { rejectValue: string }
>('marketData/validateIrIndex', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.validateIrIndex(payload);
    const data = response.data.data;

    if (data && Object.keys(data).length > 0) {
      toastMessage.success('IR index validated successfully');
    }

    return data;
  } catch (error: any) {
    const message = error.message || 'Failed to validate IR index';
    toastMessage.error(message);
    return rejectWithValue(message);
  }
});

// Thunk to download IR Forward Curve Report
export const downloadIrForwardCurveReport = createAsyncThunk<
  FileS3ResponseTypes,
  DownloadIrForwardCurveReportPayloadTypes,
  { rejectValue: string }
>(
  'marketData/downloadIrForwardCurveReport',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadIrForwardCurveReport(payload);
      const data = response.data.data;
      toastMessage.success('IR forward curve report is being generated');
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(
        message || 'Failed to generate IR forward curve report',
      );
      return rejectWithValue(
        error.message || 'Failed to generate IR forward curve report',
      );
    }
  },
);

// Thunk to download Securities Data Report
export const downloadSecuritiesDataReport = createAsyncThunk<
  FileS3ResponseTypes,
  DownloadSecuritiesDataReportPayloadTypes,
  { rejectValue: string }
>(
  'marketData/downloadSecuritiesDataReport',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadSecuritiesDataReport(payload);
      const data = response.data.data;
      toastMessage.success('Securities data report is being generated');
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(
        message || 'Failed to generate securities data report',
      );
      return rejectWithValue(
        error.message || 'Failed to generate securities data report',
      );
    }
  },
);

// Thunk to download IR Analysis Report and handle side effects
export const downloadIrAnalysisReport = createAsyncThunk<
  void,
  DownloadIrAnalysisPayloadTypes,
  { rejectValue: string }
>(
  'reporting/download-ir-analysis-report',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.donwloadIrAnalysisReport(payload);
      const data = response.data.data;
      downloadS3File(data.file_s3_url, data.file_name);
      toastMessage.success('Successfully Downloaded');
      return;
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(
        message ||
          'An unknown error occurred in downloadIrAnalysisReport request',
      );
      return rejectWithValue(
        error.message ||
          'An unknown error occurred in downloadIrAnalysisReport request',
      );
    }
  },
);

// Thunk to download RFR Report
export const downloadInterestCostCalcReport = createAsyncThunk<
  FileS3ResponseTypes,
  DownloadInterestCostCalcReportPayloadTypes,
  { rejectValue: string }
>('reporting/download-rfr-report/', async (payload, { rejectWithValue }) => {
  try {
    const response =
      await apiInstance.marketData.downloadInterestCostCalcReport(payload);
    const data = response.data.data;
    toastMessage.success('Interest cost calculation report is being generated');
    return {
      file_path: data.file_path,
      file_name: data.file_name,
      file_s3_url: data.file_s3_url,
    };
  } catch (error: any) {
    const message = getThunkError(error);
    toastMessage.error(
      message || 'Failed to generate interest cost calculation report',
    );
    return rejectWithValue(
      error.message || 'Failed to generate interest cost calculation report',
    );
  }
});

// Thunk to download Historical Forward Curve Report
export const downloadHistoricalForwardCurveReport = createAsyncThunk<
  FileS3ResponseTypes,
  DownloadHistoricalForwardCurveReportPayloadTypes,
  { rejectValue: string }
>(
  'market-data/download-historical-fwd-curve-report/',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadHistoricalForwardCurveReport(
          payload,
        );
      const data = response.data.data;
      toastMessage.success(
        'Historical forward curve report is being generated',
      );
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(
        message || 'Failed to generate historical forward curve report',
      );
      return rejectWithValue(
        error.message || 'Failed to generate historical forward curve report',
      );
    }
  },
);

// Thunk to download Hairy Chart Report
export const downloadHairyChartReport = createAsyncThunk<
  { file_path: string; file_name: string; file_s3_url: string },
  Record<string, any>,
  { rejectValue: string }
>(
  'market-data/download-hairy-chart-report/',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadHairyChartReport(payload);
      const data = response.data.data;
      toastMessage.success('Hairy chart report is being generated');
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      const message = getThunkError(error);

      toastMessage.error(message || 'Failed to generate hairy chart report');
      return rejectWithValue(
        error.message || 'Failed to generate hairy chart report',
      );
    }
  },
);

// Thunk to download IR Differencial By Currency Report
export const downloadIrDifferencialCurrencyReport = createAsyncThunk<
  IrDiffByCurrencyResponseTypes,
  DownloadIrDifferencialCurrencyReportPaylaodTypes,
  { rejectValue: string }
>(
  'market-data/return-ir-differential/',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadIrDifferencialCurrencyReport(
          payload,
        );
      const data = response.data.data;
      toastMessage.success('IR differential report calculated successfully');
      return {
        forward_rate: data.forward_rate,
        ir_differential: data.ir_differential,
        spot_rate: data.spot_rate,
        text: data.text,
      };
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(
        message || 'Failed to calculate IR differential report',
      );
      return rejectWithValue(
        error.message || 'Failed to calculate IR differential report',
      );
    }
  },
);

// Thunk to email IR Forward Curve Report
export const emailIrForwardCurveReport = createAsyncThunk<
  { message: string },
  { run_date: string; indices: string[]; frequency: number; years: number },
  { rejectValue: string }
>(
  'marketData/emailIrForwardCurveReport',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.emailIrForwardCurveReport(payload);
      const data = response.data.data;
      toastMessage.success('IR forward curve report emailed successfully');
      return { message: data.message };
    } catch (error: any) {
      const message =
        error.message || 'Failed to email IR forward curve report';
      toastMessage.error(message);
      return rejectWithValue(message);
    }
  },
);

// Thunk to get the Currencies
export const currenciesMarketData = createAsyncThunk<
  { currencies: any[]; message: string },
  void,
  { rejectValue: string }
>('marketData/getCurrencies', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.currency.getCurrencies();
    const data = response.data.data;
    const currencies = data.data.currencies;
    return { currencies: currencies, message: data.message };
  } catch (error: any) {
    const message = error.message || 'Failed to fetch currencies';
    toastMessage.error(message);
    return rejectWithValue(message);
  }
});

// Thunk to get the Filtered Currencies
export const filteredCurrenciesMarketData = createAsyncThunk<
  { currencies: any[]; message: string },
  void,
  { rejectValue: string }
>('marketData/getFilteredCurrencies', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.currency.getFilteredCurrencies();
    const data = response.data.data;
    const currencies = data.data;
    return { currencies: currencies, message: data.message };
  } catch (error: any) {
    const message = error.message || 'Failed to fetch filtered currencies';
    toastMessage.error(message);
    return rejectWithValue(message);
  }
});

// Thunk to download FX Forward Curve Report
export const reportFxForwardCurves = createAsyncThunk<
  any,
  {
    curveDate: string | null;
    spotRateIncluded: boolean;
    currencyPairs: { left: string | null; right: string | null }[];
  },
  { rejectValue: string }
>(
  'reports/fxForwardCurvesReport',
  async (
    { curveDate, currencyPairs, spotRateIncluded },
    { rejectWithValue },
  ) => {
    try {
      const payload = {
        runDate: curveDate,
        spot_rate: spotRateIncluded,
        pairs: currencyPairs.map((pair) => [pair.left || 1, pair.right || 1]),
      };

      const response =
        await apiInstance.marketData.reportFxForwardCurves(payload);

      toastMessage.success('FX forward curves report is being generated');
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error_msg ||
        'Failed to generate FX forward curves report';
      toastMessage.error(message);
      return rejectWithValue(error.response?.data || message);
    }
  },
);

// Thunk to run IR Differential
export const fetchIRDifferential = createAsyncThunk<
  any,
  {
    calculationDate: string;
    forwardDate: string;
    baseCurrency: number;
    localCurrency: number;
  },
  { rejectValue: string }
>(
  'marketData/fetchIRDifferential',
  async (
    { calculationDate, forwardDate, baseCurrency, localCurrency },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiInstance.marketData.reportIRDifferential(
        calculationDate,
        forwardDate,
        baseCurrency,
        localCurrency,
      );
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error_msg ||
        'Failed to fetch Interest Rate Differential';
      toastMessage.error(message);
      return rejectWithValue(error.response?.data || message);
    }
  },
);

// Thunk to run Historical IR Differential
export const fetchHistoricalIRDifferential = createAsyncThunk<
  any,
  {
    startDate: string;
    endDate: string;
    forwardIncrements: { id: string; label: string }[];
    currencyPairs: { left: number; right: number }[];
  },
  { rejectValue: string }
>(
  'marketData/fetchHistIRDifferential',
  async (
    { startDate, endDate, forwardIncrements, currencyPairs },
    { rejectWithValue },
  ) => {
    try {
      const transformedCurrencyPairs = currencyPairs.map((pair) => ({
        left_currency: pair.left,
        right_currency: pair.right,
      }));

      const response = await apiInstance.marketData.reportHistIRDifferential(
        startDate,
        endDate,
        forwardIncrements,
        transformedCurrencyPairs,
      );
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error_msg ||
        'Failed to fetch Historical IR Differential';
      toastMessage.error(message);
      return rejectWithValue(error.response?.data || message);
    }
  },
);

// Thunk to get the CommoditiesIndex
export const fetchCommoditiesIndices = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>('marketData/fetchCommoditiesIndices', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.returnCommoditiesIndices();
    return response.data;
  } catch (error: any) {
    const message =
      error.response?.data?.error_msg || 'Failed to fetch commodities indices';
    toastMessage.error(message);
    return rejectWithValue(error.response?.data || message);
  }
});

// Thunk to Check Commodity Data Availability
export const checkCommodityAvailability = createAsyncThunk<
  any,
  { runDate: string; indices: number[] },
  { rejectValue: string }
>(
  'marketData/checkCommodityAvailability',
  async ({ runDate, indices }, { rejectWithValue }) => {
    try {
      const response = await apiInstance.marketData.checkCommodityAvailability({
        run_date: runDate,
        indices,
      });
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error_msg ||
        'Failed to check commodity availability';
      toastMessage.error(message);
      return rejectWithValue(error.response?.data || message);
    }
  },
);

// Thunk to Download Commodity Forward Curve report
export const fetchCommodityForwardCurveReport = createAsyncThunk<
  any,
  { runDate: string; indices: number[]; frequency: string },
  { rejectValue: string }
>(
  'marketData/fetchCommodityForwardCurveReport',
  async ({ runDate, indices, frequency }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadCommodityForwardCurveReport({
          run_date: runDate,
          indices,
          frequency,
        });
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error_msg ||
        'Failed to download commodity forward curve report';
      toastMessage.error(message);
      return rejectWithValue(error.response?.data || message);
    }
  },
);

// Thunk to get the Time Frenquency
export const fetchIRTimeFrequencies = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>('interestRate/fetchTimeFrequencies', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.getTimeFrequencyIR();
    if (response.data.success) {
      return response.data.data;
    }
    return [];
  } catch (error: any) {
    const message =
      error.response?.data?.error_msg || 'Failed to fetch time frequencies';
    toastMessage.error(message);
    return rejectWithValue(error.response?.data || message);
  }
});

// Thunk to download FX Analysis
export const fetchFXAnalysis = createAsyncThunk<
  any,
  {
    leftCurrencyID: number;
    rightCurrencyID: number;
    runDate: string;
    numDaysToForecast: number;
  },
  { rejectValue: string }
>('interestRate/fetchFXAnalysis', async (payload, { rejectWithValue }) => {
  try {
    const response =
      await apiInstance.marketData.downloadFXAnalysisReport(payload);

    if (response.data.success) {
      return response.data;
    }

    toastMessage.error('Failed to fetch FX Analysis report.');
    return rejectWithValue('Failed to fetch FX Analysis report.');
  } catch (error: any) {
    const message =
      error.response?.data?.error_msg || 'Failed to fetch FX Analysis report';
    toastMessage.error(message);
    return rejectWithValue(error.response?.data || message);
  }
});

// Thunk to fetch Business Days
export const fetchBusinessDays = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>('marketData/fetchBusinessDays', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.getBusinessDays();
    return response.data;
  } catch (error: any) {
    const message =
      error.response?.data?.error_msg || 'Failed to fetch business days';
    toastMessage.error(message);
    return rejectWithValue(error.response?.data || message);
  }
});

// Thunk to download RFR Report
export const downloadRfrReport = createAsyncThunk<
  any,
  {
    notional: string;
    runDate: string;
    startDate: string;
    endDate: string;
    index: string;
    selectedBusinessDays: number[];
    lagDays: number;
    observationShift: string;
  },
  { rejectValue: string }
>('interestRate/downloadRfrReport', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.downloadRfrReport(payload);
    if (response.data.success) {
      toastMessage.success('RFR report is being generated');
      return response.data;
    }
    toastMessage.error('Failed to download RFR report.');
    return rejectWithValue('Failed to download RFR report.');
  } catch (error: any) {
    const message =
      error.response?.data?.error_msg || 'Failed to download RFR report';
    toastMessage.error(message);
    return rejectWithValue(error.response?.data || message);
  }
});

// Thunk for Correlated Market Data Simulations
export const downloadCorrelatedMarketDataSimulations = createAsyncThunk<
  any,
  {
    runDate: string;
    pairs: { index: string; currency: string }[];
    num_of_sims: number;
    tenor: number;
    correlation_history: number;
  },
  { rejectValue: string }
>(
  'marketData/downloadCorrelatedMarketDataSimulations',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadCorrelatedMarketDataSimulations(
          payload,
        );
      if (response.data.success) {
        toastMessage.success(
          'Correlated market data simulations report is being generated',
        );
        return response.data;
      }
      toastMessage.error(
        'Failed to download correlated market data simulations report.',
      );
      return rejectWithValue(
        'Failed to download correlated market data simulations report.',
      );
    } catch (error: any) {
      const message =
        error.response?.data?.error_msg ||
        error.response?.data.message ||
        'Failed to download correlated market data simulations report';
      toastMessage.error(message);
      return rejectWithValue(error.response?.data || message);
    }
  },
);

// Thunks to fetch all Securities
export const fetchSecurities = createAsyncThunk<
  { securities: { id: number; label: string }[] },
  void,
  { rejectValue: string }
>('reporting/fetchSecurities', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.fetchAllSecurities();
    return {
      securities: response.data.map(
        (security: { security_id: number; description: string }) => ({
          id: security.security_id,
          label: security.description,
        }),
      ),
    };
  } catch (error: any) {
    const message = error.message || 'Failed to fetch securities';
    toastMessage.error(message);
    return rejectWithValue(message);
  }
});

// Thunks to download the Security Data
export const downloadSecurityDataWidgetReport = createAsyncThunk<
  { file_path: string; file_name: string },
  { start_date: string; end_date: string; securities: string[] },
  { rejectValue: string }
>(
  'reporting/downloadSecurityDataWidgetReport',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadSecurityDataWidgetReport(payload);
      const data = response.data.data;
      return {
        file_path: data.file_path,
        file_name: data.file_name,
      };
    } catch (error: any) {
      const message =
        error.message || 'Failed to download security data report';
      toastMessage.error(message);
      return rejectWithValue(message);
    }
  },
);

// Thunk to download Market Update Report
export const downloadMarketUpdateReport = createAsyncThunk<
  FileS3ResponseTypes,
  { run_date: string },
  { rejectValue: string }
>(
  'marketData/downloadMarketUpdateReport',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadMarketUpdateReport(payload);
      const data = response.data.data;
      if (data.file_s3_url) {
        downloadS3File(data.file_s3_url, data.file_name);
        toastMessage.success('Market update report downloaded successfully');
      }
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      const message =
        getThunkError(error) || 'Failed to download market update report';
      toastMessage.error(message);
      return rejectWithValue(message);
    }
  },
);

// Thunk to download IR Caplet Volatility
export const downloadIrCapletVolatility = createAsyncThunk<
  FileS3ResponseTypes,
  IrCapletVolatilityBackendRequest,
  { rejectValue: string }
>(
  'market-data/return-ir-caplet-vol-surface',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadIrCapletVolatility(payload);
      const data = response.data.data;
      if (data.file_s3_url) {
        downloadS3File(data.file_s3_url, data.file_name);
        toastMessage.success(
          'IR caplet volatility report downloaded successfully',
        );
      }
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(
        message || 'Failed to download IR caplet volatility report',
      );
      return rejectWithValue(
        error.message || 'Failed to download IR caplet volatility report',
      );
    }
  },
);

// Thunk to download FX Option Volatility
export const downloadFxOptionVolatility = createAsyncThunk<
  FileS3ResponseTypes,
  FxOptionVolatilityBackendRequest,
  { rejectValue: string }
>(
  'market-data/return-fx-vol-surface/',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadFxOptionVolatility(payload);
      const data = response.data.data;
      toastMessage.success('FX option volatility report is being generated');
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(
        message || 'Failed to generate FX option volatility report',
      );
      return rejectWithValue(
        error.message || 'Failed to generate FX option volatility report',
      );
    }
  },
);

// Thunk to download CDS Curve Report
export const downloadCDSCurveReport = createAsyncThunk<
  FileS3ResponseTypes,
  DownloadCDSCurveReportPayloadTypes,
  { rejectValue: string }
>('marketData/downloadCDSCurveReport', async (payload, { rejectWithValue }) => {
  try {
    const response =
      await apiInstance.marketData.downloadCDSCurveReport(payload);
    const data = response.data.data;
    toastMessage.success('CDS curve report is being generated');
    return {
      file_path: data.file_path,
      file_name: data.file_name,
      file_s3_url: data.file_s3_url,
    };
  } catch (error: any) {
    const message = getThunkError(error);
    toastMessage.error(message || 'Failed to generate CDS curve report');
    return rejectWithValue(
      error.message || 'Failed to generate CDS curve report',
    );
  }
});

// Thunk to download Historical Ir Diff By Currency
export const downloadHistoricalIrDiffByCurrency = createAsyncThunk<
  FileS3ResponseTypes,
  HistoricalIrDifferencialByCurrencyPayload,
  { rejectValue: string }
>(
  'marketData/download-historical-ir-differential-report/',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadHistoricalIrDiffByCurrency(
          payload,
        );
      const data = response.data.data;
      toastMessage.success(
        'Historical IR differential report is being generated',
      );
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(
        message || 'Failed to generate historical IR differential report',
      );
      return rejectWithValue(
        error.message || 'Failed to generate historical IR differential report',
      );
    }
  },
);

// Thunk to download Discount Factors
export const downloadDiscountFactors = createAsyncThunk<
  FileS3ResponseTypes,
  DiscountFactorsPayload,
  { rejectValue: string }
>(
  'market-data/download-discount-factor-report/',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadDiscountFactors(payload);
      const data = response.data.data;
      toastMessage.success('Discount factors report is being generated');
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(
        message || 'Failed to generate discount factors report',
      );
      return rejectWithValue(
        error.message || 'Failed to generate discount factors report',
      );
    }
  },
);

// Thunk to download FX Spot History report
export const downloadFxSpotHistoryReport = createAsyncThunk<
  FileS3ResponseTypes,
  FxSpotHistoryReportPayload,
  { rejectValue: string }
>('market-data/fx-spot-history-report', async (payload, { rejectWithValue }) => {
  try {
    const response =
      await apiInstance.marketData.downloadFxSpotHistoryReport(payload);
    const data = response.data.data;
    return {
      file_path: data.file_path,
      file_name: data.file_name,
      file_s3_url: data.file_s3_url,
    };
  } catch (error: any) {
    const message = getThunkError(error);
    toastMessage.error(message || 'Failed to generate FX spot history report');
    return rejectWithValue(
      error.message || 'Failed to generate FX spot history report',
    );
  }
});

// Thunk to download Fx Analysis
export const downloadFxAnalysis = createAsyncThunk<
  FileS3ResponseTypes,
  FxAnalysisPayload,
  { rejectValue: string }
>('market-data/fx-analysis', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.downloadFxAnalysis(payload);
    const data = response.data.data;
    toastMessage.success('FX analysis report is being generated');
    return {
      file_path: data.file_path,
      file_name: data.file_name,
      file_s3_url: data.file_s3_url,
    };
  } catch (error: any) {
    const message = getThunkError(error);
    toastMessage.error(message || 'Failed to generate FX analysis report');
    return rejectWithValue(
      error.message || 'Failed to generate FX analysis report',
    );
  }
});

// Thunk to download Correlated Market Data Report
export const downloadCorrelatedMarketDataReport = createAsyncThunk<
  FileS3ResponseTypes,
  CorrelatedMarketDataPayload,
  { rejectValue: string }
>(
  'market-data/download-monte-carlo-sims-report/',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadCorrelatedMarketDataReport(
          payload,
        );
      const data = response.data.data;
      toastMessage.success('Correlated market data report is being generated');
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(
        message || 'Failed to generate correlated market data report',
      );
      return rejectWithValue(
        error.message || 'Failed to generate correlated market data report',
      );
    }
  },
);
